import { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from "@angular/core";

import { ColDef } from "ag-grid-enterprise";

import { EudrDdsPageService } from "@components/reports/eudr/dds/eudr-dds-page.service";
import { ReportsEudrModel } from "@components/reports/eudr/reports-eudr.model";
import { EudrDdsTableModel as Model } from "@components/settings/settings-extensions/settings-eudr/eudr-table/eudr-table.model";
import { CountryCellRendererComponent, LinkCellRendererComponent } from "@shared/cell-renderers";
import { TextConstants } from "@shared/constants";
import { RouteEnum, TableEnum } from "@shared/enums";
import { EudrDds } from "@shared/interfaces/eudr-dds.interface";
import { NotificationService, CommonService } from "@shared/services";
import { EudrDdsService } from "@shared/services/api/eudr-dds.service";
import { RouterService } from "@shared/services/router.service";
import { ColumnUtils, LocalizationUtils } from "@shared/utils";

@Component({
  standalone: false,
  selector: "app-eudr-table",
  templateUrl: "./eudr-table.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EudrTableComponent implements AfterViewInit {
  public areButtonsEnabled = input<boolean>(true);

  public isSearchEnabled = input<boolean>(true);

  public isRecordStateFilterEnabled = input<boolean>(false);

  public isPaginatorEnabled = input<boolean>(true);

  public isFixedBottomPaginator = input<boolean>(false);

  public isSaveTableState = input<boolean>(false);

  public isLoading = signal(true);

  public rowData = signal<Model.IRowData[]>([]);

  public columnDefs = signal<ColDef[]>([]);

  public readonly table = input<TableEnum>(TableEnum.ADMIN_EUDR);

  private readonly routerService = inject(RouterService);

  private readonly eudrDdsService: EudrDdsService = inject(EudrDdsService);

  private readonly commonService: CommonService = inject(CommonService);

  private readonly notificationService: NotificationService = inject(NotificationService);

  private readonly eudrDdsPageService: EudrDdsPageService = inject(EudrDdsPageService);

  public isDdsServiceUnavailable = signal(false);

  public readonly serviceUnavailableMsg = TextConstants.SERVICE_UNAVAILABLE;

  public async ngAfterViewInit(): Promise<void> {
    this.setColumnDefs();
    await this.getAll();
  }

  private setColumnDefs(): void {
    const columnDefs: ColDef[] = [
      {
        headerName: $localize`:@@shared.internalreferencenumber:Internal reference number`,
        field: "internalReferenceNumber",
        lockVisible: true,
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          linkRoute: RouteEnum.EUDR_DDS,
          linkRouteIdParam: "id",
          openInNewTab: true,
        },
      },
      {
        headerName: $localize`:@@eudr-table.activitytype:Activity type`,
        field: "activityTypeDisplayText",
      },
      {
        headerName: $localize`:@@shared.countryofactivity:Country of activity`,
        field: "address.countryName",
        cellRenderer: CountryCellRendererComponent,
      },
      ColumnUtils.tags($localize`:@@shared.status:Status`, "statusTags"),
    ];

    this.columnDefs.set(columnDefs);
  }

  public onViewDetails = async (ddsReference: EudrDds.DdsReference): Promise<void> => {
    this.routerService.openNewTab(`/${RouteEnum.EUDR_DDS}/${ddsReference.id}`);
  };

  public async getAll(): Promise<void> {
    this.isDdsServiceUnavailable.set(false);
    this.isLoading.set(true);

    try {
      const rowData: Model.IRowData[] = [];
      const ddsReferences = await this.eudrDdsService.getAll();
      const results = await this.getStatementResults(ddsReferences);

      for (const { ddsReference, statement } of results) {
        rowData.push(this.getParsedRow(ddsReference, statement));
      }

      this.rowData.set(rowData);
      this.isLoading.set(false);
    } catch (error) {
      this.notificationService.showError(error);

      if (error.status === 503) {
        this.isDdsServiceUnavailable.set(true);
      }
    }
  }

  private async getStatementResults(
    ddsReferences: EudrDds.DdsReference[],
  ): Promise<{ ddsReference: EudrDds.DdsReference; statement: EudrDds.DdsDetails }[]> {
    const statementPromises = ddsReferences.map(async (ddsReference) => {
      try {
        const statement = await this.eudrDdsService.getStatement(ddsReference.id);

        return { ddsReference, statement };
      } catch (error) {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          return { ddsReference, statement: null };
        }
        throw error;
      }
    });

    return Promise.all(statementPromises);
  }

  private getParsedRow(
    ddsReference: EudrDds.DdsReference,
    statement: EudrDds.DdsDetails,
  ): Model.IRowData {
    return {
      ...ddsReference,
      statusTags: [
        {
          title: LocalizationUtils.getStatus(ddsReference.status),
          color: this.eudrDdsPageService.getStatusTagColor(ddsReference.status),
        },
      ],
      address: {
        country: statement?.statement?.countryOfActivity,
        countryName: statement
          ? this.commonService.getCountryName(statement.statement?.countryOfActivity)
          : "-",
      },
      activityTypeDisplayText: statement?.statement?.activityType
        ? LocalizationUtils.getEudrActivityType(
            statement.statement?.activityType as ReportsEudrModel.ActivityEnum,
          )
        : "-",
    };
  }
}
